<template>
  <div class='calendar-container'>
    <div class="header-container" :style="{ width: `${recordListContainerWidth}px`}">
      <div v-for="(item, index) in selectedDateRange" class="day" :key="index" :class="item.isToday && 'today'">
        <div class="left">{{ item.dayOfMonth }}</div>
        <div class="right">{{ item.dayOfWeek }}</div>
      </div>
    </div>
    <div class="calendar-table" ref="calendarTable" :style="{ height: `${cellHeight * 9 + 21 }px`}">
      <div class="time-list">
        <div class="current-time" :style="currentTimeLineStyle">{{ currentTime }}</div>
        <div
          v-for="(time, index) in timeList"
          class="time"
          :key="index"
          :class="(index > 17 || index < 9) && 'no-working-hours'"
          :style="{ height: `${cellHeight}px`}"
        >
          <div class="text">{{ time }}</div>
        </div>
      </div>
      <div class="record-list-container" :style="{ width: `${recordListContainerWidth}px`}">
        <div v-for="(item, index) in selectedDateRange" class="record-list" ref="recordList" :key="index" :style="{ height: `${ cellHeight * 24 }px`}">
          <div class="current-time-line" :class="item.isToday && 'today'" :style="currentTimeLineStyle"></div>
          <template v-for="(record, recordIndex) in item.records">
            <el-popover
              placement="right-start"
              trigger="click"
              popper-class="interview-record-children-popover"
              :visible-arrow="false"
              :key="record._vid"
              :disabled="record.end - record.start > 1.5"
            >
              <Record
                class="record-preview"
                showOperation
                :id="record._vid"
                :status="record.status"
                :interviewTime="`${record.startTime} - ${record.endTime}`"
                :start="record.start"
                :statusName="getInterviewStatusName(record.status)"
                :end="record.end"
                :jobName="record.jobName"
                :hiringManagerNames="record.hiringManagerNames"
                :candidateNames="record.candidateNames"
                :candidateList="record.candidate"
                :canEdit="record.can_edit"
                :class="record.statusClass"
                :style="{ width: `${cellWidth}px` }"
                :noAction="record.noAction"
                :disabledTooltip="record.disabledTooltip"
                @edit="handleEdit($event, record)"
                @cancel="handleCancel($event, record)"
                @resend="handleResend($event, record)"
                @sendNew="handleSendNew($event, record)"
                @sendRating="handleSendRating($event, record)"
                @confirmInterview="handleConfirmInterview($event, record)"
                @openCandidateDetail="openCandidateDetail($event, record)"
              />
              <template #reference>
                <!-- 小于半小时显示形式 -->
                <Record
                  v-if="record.end - record.start <= 0.5"
                  class="record half-hour"
                  showRightArrowIcon
                  :id="record.uuid"
                  :status="record.status"
                  :start="record.start"
                  :end="record.end"
                  :durationLineLength="`${(record.durationEnd - record.start) * cellHeight - 2}px`"
                  :jobName="record.jobName"
                  :class="[
                    (record.hasChildren || record.children && record.children.length > 0) && 'has-children',
                    (record.end - record.start <= 1.5) && 'is-incomplete',
                    record.statusClass
                  ]"
                  :style="{
                    top: `${record.start * cellHeight + 1}px`,
                    height: `${(record.end - record.start) * cellHeight - 2}px`,
                    justifyContent: 'space-between'
                  }"
                  :noAction="record.noAction"
                  :disabledTooltip="record.disabledTooltip"
                />
                <!-- 小于1小时显示形式 -->
                <Record
                  v-else-if="record.end - record.start <= 1"
                  class="record an-hour"
                  showRightArrowIcon
                  :id="record.uuid"
                  :status="record.status"
                  :statusName="getInterviewStatusName(record.status)"
                  :start="record.start"
                  :end="record.end"
                  :durationLineLength="`${(record.durationEnd - record.start) * cellHeight - 2}px`"
                  :jobName="record.jobName"
                  :class="[
                    (record.hasChildren || record.children && record.children.length > 0) && 'has-children',
                    (record.end - record.start <= 1.5) && 'is-incomplete',
                    record.statusClass
                  ]"
                  :style="{
                    top: `${record.start * cellHeight + 1}px`,
                    height: `${(record.end - record.start) * cellHeight - 2}px`,
                    justifyContent: 'space-between'
                  }"
                  :noAction="record.noAction"
                  :disabledTooltip="record.disabledTooltip"
                />
                <!-- 小于两小时，大于两小时的情况 -->
                <Record
                  v-else
                  class="record"
                  :id="record.uuid"
                  :status="record.status"
                  :statusName="getInterviewStatusName(record.status)"
                  :interviewTime="record.end - record.start > 1.5 ? `${record.startTime} - ${record.endTime}` : ''"
                  :start="record.start"
                  :end="record.end"
                  :durationLineLength="`${(record.durationEnd - record.start) * cellHeight - 2}px`"
                  :jobName="record.jobName"
                  :hiringManagerNames="record.hiringManagerNames"
                  :candidateNames="record.end - record.start > 1.5 ? record.candidateNames : ''"
                  :candidateList="record.candidate"
                  :canEdit="record.can_edit"
                  :showOperation="record.end - record.start > 1.5"
                  :showRightArrowIcon="record.end - record.start <= 1.5"
                  :class="[
                    (record.hasChildren || record.children && record.children.length > 0) && 'has-children',
                    (record.end - record.start <= 1.5) && 'is-incomplete',
                    record.statusClass
                  ]"
                  :style="{
                    top: `${record.start * cellHeight + 1}px`,
                    height: `${(record.end - record.start) * cellHeight - 2}px`,
                    justifyContent: record.end - record.start <= 1.5 && 'space-between'
                  }"
                  :noAction="record.noAction"
                  :disabledTooltip="record.disabledTooltip"
                  @edit="handleEdit($event, record)"
                  @cancel="handleCancel($event, record)"
                  @resend="handleResend($event, record)"
                  @sendNew="handleSendNew($event, record)"
                  @sendRating="handleSendRating($event, record)"
                  @confirmInterview="handleConfirmInterview($event, record)"
                  @openCandidateDetail="openCandidateDetail($event, record)"
                />
              </template>
            </el-popover>
            <el-popover
              v-if="record.children && record.children.length > 0"
              placement="right-start"
              trigger="click"
              popper-class="interview-record-children-popover"
              :visible-arrow="false"
              :key="recordIndex"
            >
              <div class="child-container" :style="{ width: `${cellWidth}px` }">
                <Record
                  v-for="(child, childIndex) in record.children"
                  class="record-child"
                  showOperation
                  :key="childIndex"
                  :class="child.statusClass"
                  :id="child.uuid"
                  :status="child.status"
                  :statusName="getInterviewStatusName(child.status)"
                  :interviewTime="`${child.startTime} - ${child.endTime}`"
                  :jobName="child.jobName"
                  :hiringManagerNames="child.hiringManagerNames"
                  :candidateNames="child.candidateNames"
                  :candidateList="child.candidate"
                  :canEdit="child.can_edit"
                  :noAction="child.noAction"
                  :disabledTooltip="child.disabledTooltip"
                  @edit="handleEdit($event, child)"
                  @cancel="handleCancel($event, child)"
                  @resend="handleResend($event, child)"
                  @sendNew="handleSendNew($event, child)"
                  @sendRating="handleSendRating($event, child)"
                  @confirmInterview="handleConfirmInterview($event, child)"
                  @openCandidateDetail="openCandidateDetail($event, child)"
                />
              </div>
              <!-- 高度：不足半小时按照半小时算 -->
              <div
                v-if="record.children && record.children.length > 0"
                slot="reference"
                class="record-children"
                :key="recordIndex"
                :style="{
                  top: `${record.children[0].start * cellHeight + 1}px`,
                  height: `${ (getChildrenHeight(record.children) < 0.5 ? 0.5 : getChildrenHeight(record.children)) * cellHeight - 2 }px`
                }"
              >
              <!-- 真实左侧时间线长度 -->
              <div class="duration-line" :style="{ height: `${ getChildrenHeight(record.children) * cellHeight - 2 }px` }"></div>
              <div  class="show-children-btn">
                <i class="el-icon-caret-right"></i>
              </div>
            </div>
            </el-popover>
          </template>
        </div>
      </div>
    </div>
    <RectLoading v-show="loading || calendarTableLoading" class="loading"/>
  </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { INTERVIEW_STATUS } from '@/constant/interview'
import RectLoading from '@/components/loading/rectLoading.vue'
import Record from './record.vue'

export default {
  components: {
    RectLoading,
    Record
  },
  props: {
    interviewRecords: {
      type: Array,
      default: () => []
    },
    selectedDateRange: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: false
    },
    calendarTableLoading: {
      type: Boolean,
      required: false
    },
    showCalendar: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      cellHeight: 56,
      cellWidth: null,
      INTERVIEW_STATUS,
      recordListContainerWidth: null
    }
  },
  computed: {
    timeList () {
      const list = []
      for (let i = 0; i <= 24; i++) {
        list.push(`${i < 10 ? '0' : ''}${i}:00`)
      }
      return list
    },
    currentTime () {
      return moment().format('HH:mm')
    },
    currentTimeLineStyle () {
      const current = moment().format('HH:mm')
      const duration = current.split(':')
      const top = parseInt(duration[0]) + parseInt(duration[1]) / 60
      return {
        top: `${top * this.cellHeight}px`
      }
    }
  },
  mounted () {
    window.onresize = () => {
      this.recordListContainerWidth = null
      this.$nextTick(() => {
        this.computedCellWidth()
        this.computedRecordListContainerWidth()
      })
    }
  },
  destroyed () {
    window.onresize = null
  },
  methods: {
    handleExpendDetail (record) {
      record.childrenExpend = !record.childrenExpend
    },
    getChildrenHeight (children) {
      return Math.max.apply(null, _.map(children, child => child.durationEnd)) - Math.min.apply(null, _.map(children, child => child.start))
    },
    handleScroll () {
      this.$nextTick(() => {
        const calendarTableEl = this.$refs.calendarTable
        calendarTableEl.scrollTo({ top: this.cellHeight * 9 - 11 })
      })
    },
    computedCellWidth () {
      this.cellWidth = this.$refs.recordList[0].offsetWidth || 140
    },
    computedRecordListContainerWidth () {
      this.recordListContainerWidth = this.$refs.calendarTable.offsetWidth - 53
    },
    handleEdit ($event, record) {
      this.$emit('edit', record)
    },
    handleCancel ($event, record) {
      this.$emit('cancel', record)
    },
    handleResend ($event, record) {
      this.$emit('resend', record)
    },
    handleSendNew ($event, record) {
      this.$emit('sendNew', record)
    },
    handleSendRating ($event, record) {
      this.$emit('sendRating', record)
    },
    handleConfirmInterview ($event, record) {
      this.$emit('confirmInterview', record)
    },
    openCandidateDetail ($event, record) {
      if (record.candidate?.length !== 1) {
        return
      }
      this.$emit('openCandidateDetail', record)
    },
    getInterviewStatusName (status) {
      const labelKey = _.findKey(INTERVIEW_STATUS, value => value === status)
      if (labelKey) {
        return this.$t(`${this.i18nPrefix}.label.${labelKey}`)
      }
    }
  },
  watch: {
    showCalendar: {
      immediate: true,
      handler () {
        this.handleScroll()
        this.$nextTick(() => {
          this.computedCellWidth()
          this.computedRecordListContainerWidth()
        })
      }
    }
  }
}
</script>

<style lang='scss' scoped>
.calendar-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  height: 100%;
  padding-top: 4px;
  position: relative;

  .header-container {
    display: flex;
    margin-right: 18px;
    margin-left: 35px;
    box-shadow: 0px -2px 4px 0px rgba(0,0,0,0.04), 0px 2px 4px 0px rgba(0,0,0,0.12);
    border: 1px solid #dfdfdf;
    border-radius: 6px 6px 0px 0px;
    z-index: 1;

    .day {
      flex: 1;
      padding: 10px 12px;
      border-right: 1px solid #dfdfdf;
      color: #606266;
      display: flex;
      align-items: center;

      .left {
        font-size: 20px;
        line-height: 28px;
        font-weight: 500;
      }

      .right {
        font-size: 12px;
        line-height: 17px;
        font-weight: 400;
        margin-left: 8px;
      }

      &:last-child {
        border-right: 0;
      }

      &.today {
        color: #0096cc;
      }
    }
  }

  .calendar-table {
    border-radius: 0px 0px 6px 6px;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;

    .time-list {
      position: absolute;
      left: 0;
      width: 100%;

      .time {
        font-size: 12px;
        color: #909399;
        border-bottom: 1px solid #dfdfdf;
        position: relative;
        margin-right: 1px;

        .text {
          position: absolute;
          top: -7px;
          width: 35px;
          background-color: #fff;
        }

        &::before {
          content: '';
          display: inline-block;
          position: absolute;
          left: 0;
          top: 0;
          width: 35px;
          height: 100%;
          background: #fff;
          border-right: 1px solid #dfdfdf;
        }

        &::after {
          content: '';
          display: inline-block;
          position: absolute;
          top: 50%;
          left: 35px;
          transform: translateY(-50%);
          width: 100%;
          height: 1px;
          border-bottom: 1px dashed #dfdfdf;
        }

        &:nth-last-child(2) {
          border-bottom: 0;
        }

        &:nth-child(2) {
          .text {
            top: 0;
          }
        }

        &:last-child {
          top: -6px;
          height: 0 !important;
          border-bottom: 0;

          &::after {
            display: none;
          }
        }

        &.no-working-hours {
          background-color: #f8f8f9;
        }
      }

      .current-time {
        position: absolute;
        font-size: 12px;
        margin-top: -6px;
        color: #0096cc;
        background-color: #fff;
        z-index: 1;
      }
    }

    .record-list-container {
      display: flex;
      margin-left: 35px;
      border: 1px solid #dfdfdf;
      border-top: 0;
      border-radius: 0 0 6px 6px;

      .record-list {
        flex: 1;
        position: relative;
        border-right: 1px solid #dfdfdf;

        &:last-child {
          border-right: 0
        }

        .record {
          &.is-incomplete {
            cursor: pointer;
          }
        }

        .record-children {
          position: absolute;
          left: calc(100% - 20px);
          background-color: #f0edff;
          width: 20px;
          cursor: pointer;

          .duration-line {
            content: '';
            display: block;
            width: 4px;
            position: absolute;
            top: 0;
            left: 0;
            background-color: #8e76f9;
          }

          // &::before {
          //   content: '';
          //   display: block;
          //   width: 4px;
          //   position: absolute;
          //   top: 0;
          //   left: 0;
          //   bottom: 0;
          //   background-color: #8e76f9;
          // }

          .show-children-btn {
            margin-left: 3px;
            font-size: 14px;
            color: #8d76f8;

            .expend {
              transform: rotate(90deg);
            }
          }
        }

        .current-time-line {
          position: absolute;
          height: 2px;
          width: 100%;
          background-color: rgba(0,150,204,0.39);

          &.today {
            background-color: #0096cc;

            &::before {
              content: '';
              display: block;
              width: 8px;
              height: 8px;
              background-color: #0096cc;
              position: absolute;
              left: 0;
              top: -3px;
              border-radius: 50%;
            }
          }
        }
      }
    }
  }

  .loading {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }
}
</style>
<style lang='scss'>
.interview-record-children-popover.el-popover {
  margin-left: 0;
  padding: 6px;
  max-height: 300px;
  min-width: initial;
  overflow: auto;
  box-shadow: 0px 3px 6px -4px rgba(0,0,0,0.12), 0px 6px 16px 0px rgba(0,0,0,0.08);

  div {
    font-size: 12px;
  }

  .record-child {
    position: relative;
    font-size: 12px;
    padding: 8px 10px;
    width: 100%;
    margin-bottom: 20px;

    &::before {
      content: '';
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
    }

    &::after {
      content: '';
      display: block;
      position: absolute;
      height: 1px;
      width: 100%;
      background-color: #f0f0f0;
      left: 0;
      bottom: -10px;
    }

    &:last-child {
      margin-bottom: 0;

      &::after {
        display: none;
      }
    }
  }

  .record-preview {
    position: relative;
    font-size: 12px;
    padding: 8px 10px;
    width: 100%;
  }
}
</style>
